import { subDays } from 'date-fns';
import { CalendarIcon, ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { MappedAssetSensor } from '@/common/asset-sensor';
import { ROLE } from '@/common/role';
import { timeseriesAggregationTypeLabelsArray } from '@/common/sensors/common';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePermission } from '@/hooks/use-permission';
import { cn, formatDateTime } from '@/lib/utils';
import { AccessDeniedView } from '../../AccessDeniedView';
import { MeteoTimeSeriesCharts, type MeteoTimeSeriesChartsFilters } from './TimeSeriesCharts';

export type MeteoTimeSeriesProps = {
	assetId: string;
	selectedSensor: MappedAssetSensor;
};

const defaultFilters: MeteoTimeSeriesChartsFilters = {
	startDate: undefined,
	endDate: undefined,
	aggregation: 'none',
};
export function MeteoTimeSeries({ assetId, selectedSensor }: MeteoTimeSeriesProps) {
	const { t } = useTranslation();
	const [filters, setFilters] = useState<MeteoTimeSeriesChartsFilters>(defaultFilters);
	const [showFilters, setShowFilters] = useState(false);
	const hasReadPermission = usePermission(ROLE.SENSORS_MET_TIMESERIES_READ);
	const aggregationOptions = useMemo(() => timeseriesAggregationTypeLabelsArray(t), [t]);

	useEffect(() => {
		if (!selectedSensor?.id) {
			return;
		}
		// Set default date range to last 7 days if not set
		if (!filters.startDate || !filters.endDate) {
			const currentDate = new Date();
			setFilters((fil) => ({
				...fil,
				startDate: subDays(currentDate, 7),
				endDate: currentDate,
			}));
		}
	}, [selectedSensor?.id, filters.startDate, filters.endDate]);

	const handleFilterChange =
		<T extends keyof MeteoTimeSeriesChartsFilters>(key: T) =>
		(value: MeteoTimeSeriesChartsFilters[T]) => {
			setFilters((prev) => ({
				...prev,
				[key]: value,
			}));
		};

	// If user doesn't have permission to read, show access denied message
	if (!hasReadPermission) {
		return <AccessDeniedView />;
	}

	return (
		<div className="flex-1 overflow-auto space-y-3">
			<p className="text-sm text-muted-foreground lg:hidden">
				{t('health.sensors.selectedLabel')} <span className="font-medium text-foreground">{selectedSensor.name}</span>
			</p>
			<Card>
				<CardContent className="flex flex-col gap-1">
					{/* Toggle — mobile/tablet only */}
					<div className="flex items-center justify-between lg:hidden">
						<span className="text-xs font-medium text-muted-foreground">{t('health.charts.filters')}</span>
						<button
							type="button"
							onClick={() => setShowFilters((v) => !v)}
							className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
						>
							{showFilters ? t('health.charts.hide') : t('health.charts.show')}
							<ChevronDown
								className={cn('h-3.5 w-3.5 transition-transform duration-300', showFilters && 'rotate-180')}
							/>
						</button>
					</div>

					{/* Collapsible content */}
					<div
						className={cn(
							'grid overflow-hidden transition-all duration-300 ease-in-out lg:grid-rows-[1fr]',
							showFilters ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
						)}
					>
						<div className="overflow-hidden min-h-0">
							<div className="grid grid-cols-2 lg:grid-cols-[auto_auto_1fr] gap-1">
								<div>
									<Label className="pb-2 text-xs">{t('health.charts.startDate')}</Label>
									<Popover>
										<PopoverTrigger asChild className="w-full">
											<Button
												variant="outline"
												className={cn(
													'pl-3 text-left font-normal bg-white hover:bg-transparent w-full',
													!filters.startDate && 'text-muted-foreground',
												)}
											>
												{filters.startDate ? (
													formatDateTime(filters.startDate, 'dd-MM-yyyy HH:mm')
												) : (
													<span>{t('health.charts.pickDate')}</span>
												)}
												<CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
											</Button>
										</PopoverTrigger>
										<PopoverContent className="w-auto p-0" align="start">
											<Calendar
												mode="single"
												selected={filters.startDate}
												onSelect={handleFilterChange('startDate')}
												disabled={(date) => date > new Date() || date < new Date('1900-01-01')}
												captionLayout="dropdown"
											/>
										</PopoverContent>
									</Popover>
								</div>

								<div>
									<Label className="pb-2 text-xs">{t('health.charts.endDate')}</Label>
									<Popover>
										<PopoverTrigger asChild className="w-full">
											<Button
												variant="outline"
												className={cn(
													'pl-3 text-left font-normal bg-white hover:bg-transparent w-full',
													!filters.endDate && 'text-muted-foreground',
												)}
											>
												{filters.endDate ? (
													formatDateTime(filters.endDate, 'dd-MM-yyyy HH:mm')
												) : (
													<span>{t('health.charts.pickDate')}</span>
												)}
												<CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
											</Button>
										</PopoverTrigger>
										<PopoverContent className="w-auto p-0" align="start">
											<Calendar
												mode="single"
												selected={filters.endDate}
												onSelect={handleFilterChange('endDate')}
												disabled={(date) => !!filters.startDate && date <= filters.startDate}
												captionLayout="dropdown"
											/>
										</PopoverContent>
									</Popover>
								</div>

								<div className="col-span-2 lg:col-span-1">
									<Label className="pb-2 text-xs">{t('health.charts.aggregationLabel')}</Label>
									<Select
										value={filters.aggregation}
										onValueChange={(v) =>
											handleFilterChange('aggregation')(v as MeteoTimeSeriesChartsFilters['aggregation'])
										}
									>
										<SelectTrigger className="w-full bg-white">
											<SelectValue />
										</SelectTrigger>
										<SelectContent className="bg-muted" position="popper">
											{aggregationOptions.map(({ value, label }) => (
												<SelectItem key={value} value={value} className="text-xs">
													{label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>
			<MeteoTimeSeriesCharts assetId={assetId} selectedSensor={selectedSensor} filters={filters} />
		</div>
	);
}
